import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { ParamsContext } from 'providers/ParamsContext';
import flow from 'lodash.flow';
import { updateOfferHeader } from '../actionCreators';
import {
  loadResponsibles,
  loadSources,
  loadChannels,
  loadPartnerPriorities,
  loadOfferTypes
} from 'actions/actionCreators/dictionariesActions';
import { getDocumentSubtypes } from 'selectors/dictionariesSelectors';
import OfferAssetGeneralCard from './OfferAssetGeneralCard';
import documentTypeIds from 'constants/documentTypeIds';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { RightsContext } from 'providers/RightsContext';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { ASM_RESPONSIBLES_QUERY, WORK_LOCATIONS_QUERY, DEALERS_QUERY } from '../queries';
import { emptyString } from 'constants/emptyConst';
import * as editableDocument from 'utils/model/editableDocument';
import { isMandatory } from 'utils/simple-validations';

const getValidationRules = () => ({
  responsiblePartnerId: [isMandatory()]
});

const OfferAssetGeneralDetailsContainer = ({ documentId, siteId, isFacilityFrame }) => {
  const dispatch = useDispatch();
  const { assetGeneralDataRead } = useContext(RightsContext);
  const [localOfferHeader, setLocalOfferHeader] = useState(editableDocument.create({}));
  const [isDirty, setIsDirty] = useState(false);
  const [asmResponsibleOptions, setASMResponsibleOptions] = useState([]);
  const [responsibleOptions] = useState([]);
  const [workLocations, setWorkLocations] = useState([]);
  const [dealers, setDealers] = useState([]);
  const { selfPartnerId } = useContext(ParamsContext);

  useQueryWithErrorHandling(ASM_RESPONSIBLES_QUERY, {
    onCompleted: data => {
      const responsibles = data?.automaticUserAllocationsResponsiblesByUserGroups;
      const asmResponsibles = responsibles?.asmResponsibles;
      if (asmResponsibles)
        setASMResponsibleOptions(
          asmResponsibles.map(x => {
            return { responsibleId: x.personId, name: `${x.firstName} ${x.lastName}` };
          })
        );
    }
  });

  useQueryWithErrorHandling(WORK_LOCATIONS_QUERY, {
    variables: {
      dealerId: localOfferHeader.document.responsiblePartnerId
    },
    skip: !localOfferHeader.document.responsiblePartnerId,
    onCompleted: data => {
      const workLocations = data?.workLocations;
      if (workLocations)
        setWorkLocations(
          workLocations.map(x => {
            return { id: x.id, name: x.name };
          })
        );
    }
  });

  useQueryWithErrorHandling(DEALERS_QUERY, {
    onCompleted: data => {
      const dealers = data?.dealers;
      if (dealers)
        setDealers(
          dealers.map(x => {
            return { id: x.id, name: x.name };
          })
        );
    }
  });

  const offerTypes = useSelector(
    state => getDocumentSubtypes(state, isFacilityFrame ? documentTypeIds.FacilityFrameOffer : documentTypeIds.Offer) || [],
    shallowEqual
  );
  const offerHeader = useSelector(state => state.currentOffer.header, shallowEqual);
  const sources = useSelector(state => state.currentOffer.sources || [], shallowEqual);
  const channels = useSelector(state => (state.dictionaries.channels.isLoaded ? state.dictionaries.channels.items : []), shallowEqual);
  const partnerPriorities = useSelector(
    state => (state.dictionaries.partnerPriorities.isLoaded ? state.dictionaries.partnerPriorities.items : []),
    shallowEqual
  );
  const viewType = useSelector(state => state.params.items.viewType, shallowEqual);
  const userInfo = useSelector(state => state.userInfo, shallowEqual);

  useEffect(() => {
    if (responsibleOptions.length) return;
    dispatch(loadResponsibles(emptyString, selfPartnerId)).then(data =>
      data.map(item => responsibleOptions.push({ responsibleId: item.responsibleId, responsibleName: item.name }))
    );
  }, [dispatch, offerHeader, responsibleOptions, selfPartnerId]);

  useEffect(() => {
    dispatch(loadSources());
    dispatch(loadChannels());
    dispatch(loadPartnerPriorities());
    dispatch(loadOfferTypes(isFacilityFrame ? documentTypeIds.FacilityFrameOffer : documentTypeIds.Offer));
  }, [isFacilityFrame, dispatch]);

  useEffect(() => {
    if (
      (!localOfferHeader.document.documentId && offerHeader.documentId) ||
      (!isDirty && offerHeader.offerTypeId && localOfferHeader.document.offerTypeId !== offerHeader.offerTypeId)
    ) {
      setLocalOfferHeader(editableDocument.create(offerHeader));
    }
  }, [offerHeader, localOfferHeader, setLocalOfferHeader, isDirty]);

  const updateGeneralData = (value, prop = null) => {
    setIsDirty(true);
    setLocalOfferHeader(prevState => {
      const updates = prop
        ? [editableDocument.update(prop, value, null)]
        : Object.entries(value || {}).map(([k, v]) => editableDocument.update(k, v, null));
      return flow(...updates, editableDocument.validate(getValidationRules(), true))(prevState);
    });
  };

  const onSave = () => {
    const validated = editableDocument.validate(getValidationRules(), false, localOfferHeader);
    if (!validated.validationInfo.isValid) {
      setLocalOfferHeader(validated);
      return;
    }

    const data = {
      ...validated.document,
      documentId,
      siteId
    };
    dispatch(updateOfferHeader(data, () => setIsDirty(false)));
  };

  return (
    <>
      {assetGeneralDataRead && (
        <OfferAssetGeneralCard
          generalData={localOfferHeader.document}
          sources={sources}
          channels={channels}
          partnerPriorities={partnerPriorities}
          offerTypes={offerTypes}
          updateGeneralData={updateGeneralData}
          onSave={onSave}
          processing={offerHeader.processing}
          savingGeneralData={offerHeader.savingGeneralData}
          viewType={viewType}
          isDirty={isDirty}
          isFacilityFrame={isFacilityFrame}
          asmResponsibleOptions={asmResponsibleOptions}
          responsibleOptions={responsibleOptions}
          workLocations={workLocations}
          dealers={dealers}
          userInfo={userInfo}
          validationInfo={localOfferHeader.validationInfo}
        />
      )}
    </>
  );
};

OfferAssetGeneralDetailsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  isFacilityFrame: PropTypes.bool
};

export default OfferAssetGeneralDetailsContainer;
